import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Seo } from "@/components/seo/Seo";
import { Header } from "@/components/Header";
import {
  normalizeTemplateContinuationPath,
  pendingAppTemplateContinuation,
  rememberAppTemplateContinuation,
} from "@/lib/app-templates";

const WaitingList = () => {
  const requestedContinuation = new URLSearchParams(window.location.search).get("redirect");
  const continuationPath = normalizeTemplateContinuationPath(requestedContinuation)
    ?? pendingAppTemplateContinuation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState<"user" | "expert">("user");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (continuationPath) rememberAppTemplateContinuation(continuationPath);
  }, [continuationPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-waitlist', {
        body: {
          name,
          email,
          userType,
          ...(continuationPath ? { continuationPath } : {}),
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      
      if (data?.alreadyRegistered) {
        toast({
          title: "Application received",
          description: data.message || "We already have your application and will be in touch soon.",
        });
      } else {
        toast({
          title: "Application received",
          description: "Thank you for your application.",
        });
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: error instanceof Error
          ? error.message
          : "Failed to submit your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Seo 
        title="Apply for Beta Access - Applyt"
        description="Apply for early access to Applyt's expert thinking methods and AI-powered conversations."
      />
      <Header />
      <div className="flex items-center justify-center p-4 pt-20">
        <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-3xl font-bold">Apply for Beta Access</CardTitle>
          <CardDescription>
            Get exclusive early access to Applyt
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Apply"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="text-5xl mb-4">🎉</div>
              <h3 className="text-xl font-semibold">Thank you for your application.</h3>
              <p className="text-muted-foreground">
                {continuationPath
                  ? "If accepted, we'll send an invitation. After registration, your App creation will continue automatically."
                  : "We'll review your application and be in touch soon."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
  );
};

export default WaitingList;
